/**
 * AccountPage - account security settings (password, 2FA, sessions)
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@stores/authStore';
import { authApi } from '@services/api';

export default function AccountPage() {
  const { t } = useTranslation();
  const { user, logout } = useAuthStore();

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // 2FA state
  const [is2FAEnabled, setIs2FAEnabled] = useState(user?.twoFactorEnabled ?? false);
  const [isEnabling2FA, setIsEnabling2FA] = useState(false);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [twoFAError, setTwoFAError] = useState<string | null>(null);

  // Delete account state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(false);

    if (newPassword !== confirmPassword) {
      setPasswordError(t('settings.passwordMismatch'));
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError(t('settings.passwordTooShort'));
      return;
    }

    setIsChangingPassword(true);

    try {
      await authApi.changePassword({
        currentPassword,
        newPassword,
      });
      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : 'Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleEnable2FA = async () => {
    setTwoFAError(null);
    setIsEnabling2FA(true);

    try {
      const response = await authApi.enable2FA();
      setQrCode(response.qrCode);
    } catch (err) {
      setTwoFAError(err instanceof Error ? err.message : 'Failed to enable 2FA');
      setIsEnabling2FA(false);
    }
  };

  const handleVerify2FA = async () => {
    setTwoFAError(null);

    try {
      await authApi.verify2FA({ code: verificationCode });
      setIs2FAEnabled(true);
      setQrCode(null);
      setVerificationCode('');
      setIsEnabling2FA(false);
    } catch (err) {
      setTwoFAError(err instanceof Error ? err.message : 'Invalid verification code');
    }
  };

  const handleDisable2FA = async () => {
    setTwoFAError(null);

    try {
      await authApi.disable2FA();
      setIs2FAEnabled(false);
    } catch (err) {
      setTwoFAError(err instanceof Error ? err.message : 'Failed to disable 2FA');
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;

    setDeleteError(null);
    setIsDeleting(true);

    try {
      await authApi.deleteAccount();
      logout();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : t('settings.deleteAccountFailed', 'Failed to delete account'));
      setIsDeleting(false);
    }
  };

  return (
    <div className="p-6 space-y-8">
      <div>
        <h2 className="text-lg font-medium text-neutral-900 dark:text-white">
          {t('settings.account')}
        </h2>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          {t('settings.accountDescription')}
        </p>
      </div>

      {/* Password Change */}
      <div className="border-t border-neutral-200 dark:border-neutral-700 pt-6">
        <h3 className="text-base font-medium text-neutral-900 dark:text-white mb-4">
          {t('settings.changePassword')}
        </h3>

        {passwordError && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">{passwordError}</p>
          </div>
        )}

        {passwordSuccess && (
          <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <p className="text-sm text-green-600 dark:text-green-400">
              {t('settings.passwordChanged')}
            </p>
          </div>
        )}

        <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
          <div>
            <label
              htmlFor="currentPassword"
              className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
            >
              {t('settings.currentPassword')}
            </label>
            <input
              type="password"
              id="currentPassword"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="mt-1 block w-full rounded-md border border-neutral-300 bg-white text-neutral-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-neutral-600 dark:bg-neutral-700 dark:text-white sm:text-sm"
              required
            />
          </div>

          <div>
            <label
              htmlFor="newPassword"
              className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
            >
              {t('settings.newPassword')}
            </label>
            <input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="mt-1 block w-full rounded-md border border-neutral-300 bg-white text-neutral-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-neutral-600 dark:bg-neutral-700 dark:text-white sm:text-sm"
              required
              minLength={8}
            />
          </div>

          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
            >
              {t('settings.confirmPassword')}
            </label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="mt-1 block w-full rounded-md border border-neutral-300 bg-white text-neutral-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-neutral-600 dark:bg-neutral-700 dark:text-white sm:text-sm"
              required
            />
          </div>

          <button
            type="submit"
            disabled={isChangingPassword}
            className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {isChangingPassword ? t('common.saving') : t('settings.changePassword')}
          </button>
        </form>
      </div>

      {/* Two-Factor Authentication */}
      <div className="border-t border-neutral-200 dark:border-neutral-700 pt-6">
        <h3 className="text-base font-medium text-neutral-900 dark:text-white mb-4">
          {t('settings.twoFactorAuth')}
        </h3>

        {twoFAError && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">{twoFAError}</p>
          </div>
        )}

        {is2FAEnabled ? (
          <div className="flex items-center justify-between max-w-md">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-green-600 dark:text-green-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-neutral-900 dark:text-white">
                  {t('settings.twoFactorEnabled')}
                </p>
                <p className="text-xs text-neutral-500">
                  {t('settings.twoFactorEnabledDescription')}
                </p>
              </div>
            </div>
            <button
              onClick={handleDisable2FA}
              className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md"
            >
              {t('settings.disable')}
            </button>
          </div>
        ) : isEnabling2FA && qrCode ? (
          <div className="max-w-md space-y-4">
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              {t('settings.scanQRCode')}
            </p>
            <div className="flex justify-center p-4 bg-white rounded-lg">
              <img src={qrCode} alt="2FA QR Code" className="w-48 h-48" />
            </div>
            <div>
              <label
                htmlFor="verificationCode"
                className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
              >
                {t('settings.verificationCode')}
              </label>
              <input
                type="text"
                id="verificationCode"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="000000"
                maxLength={6}
                className="mt-1 block w-full rounded-md border border-neutral-300 bg-white text-neutral-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-neutral-600 dark:bg-neutral-700 dark:text-white sm:text-sm"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleVerify2FA}
                disabled={verificationCode.length !== 6}
                className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {t('settings.verify')}
              </button>
              <button
                onClick={() => {
                  setIsEnabling2FA(false);
                  setQrCode(null);
                }}
                className="px-4 py-2 text-neutral-600 dark:text-neutral-400 text-sm font-medium hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-md"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        ) : (
          <div className="max-w-md">
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
              {t('settings.twoFactorDescription')}
            </p>
            <button
              onClick={handleEnable2FA}
              className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700"
            >
              {t('settings.enableTwoFactor')}
            </button>
          </div>
        )}
      </div>

      {/* Active Sessions */}
      <div className="border-t border-neutral-200 dark:border-neutral-700 pt-6">
        <h3 className="text-base font-medium text-neutral-900 dark:text-white mb-4">
          {t('settings.activeSessions')}
        </h3>
        <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
          {t('settings.activeSessionsDescription')}
        </p>
        <div className="space-y-3 max-w-lg">
          <div className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
            <div className="flex items-center gap-3">
              <svg
                className="w-5 h-5 text-neutral-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              <div>
                <p className="text-sm font-medium text-neutral-900 dark:text-white">
                  Current Session
                </p>
                <p className="text-xs text-neutral-500">
                  Chrome on macOS - Active now
                </p>
              </div>
            </div>
            <span className="text-xs text-green-600 dark:text-green-400 font-medium">
              {t('settings.current')}
            </span>
          </div>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="border-t border-red-200 dark:border-red-800 pt-6">
        <h3 className="text-base font-medium text-red-600 dark:text-red-400 mb-4">
          {t('settings.dangerZone')}
        </h3>

        {!showDeleteConfirm ? (
          <div className="flex items-center justify-between max-w-md p-4 border border-red-200 dark:border-red-800 rounded-lg">
            <div>
              <p className="text-sm font-medium text-neutral-900 dark:text-white">
                {t('settings.deleteAccount')}
              </p>
              <p className="text-xs text-neutral-500">
                {t('settings.deleteAccountDescription')}
              </p>
            </div>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="px-3 py-1.5 text-sm text-red-600 border border-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md"
            >
              {t('settings.delete')}
            </button>
          </div>
        ) : (
          <div className="max-w-md p-4 border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 rounded-lg space-y-4">
            <p className="text-sm text-red-600 dark:text-red-400">
              {t('settings.deleteAccountWarning')}
            </p>
            {deleteError && (
              <div className="p-3 bg-red-100 dark:bg-red-900/40 rounded-md">
                <p className="text-sm text-red-700 dark:text-red-300">{deleteError}</p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-red-700 dark:text-red-400">
                {t('settings.typeDeleteToConfirm')}
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
                className="mt-1 block w-full rounded-md border-red-300 shadow-sm focus:border-red-500 focus:ring-red-500 dark:border-red-600 dark:bg-neutral-700 dark:text-white sm:text-sm"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== 'DELETE' || isDeleting}
                className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {isDeleting ? t('common.deleting') : t('settings.deleteAccountConfirm')}
              </button>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteConfirmText('');
                }}
                className="px-4 py-2 text-neutral-600 dark:text-neutral-400 text-sm font-medium hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-md"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
